import { Component } from "react";
import PropTypes from "prop-types";
import IdealStudio from "../../../../core/editor/main";
import { Row, Col, ListGroup } from "react-bootstrap";
import AppModal from "../../common/modal/AppModal";
import i18n from "i18next";
import ToggleSwitch from "@trendmicro/react-toggle-switch";
import "./LipsyncModal.scss";
import { apiFetch } from "../../../../common/http_service.es6";
import { getToken } from "../../../../common/environment_config";
import { credentialsFor } from "../../../../common/utils";
import { getMediaDuration } from "../../../utils/mediaDuration";

export default class LipsyncModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAudio: null,
      sceneAudios: [],
      isLoading: false,
      removeBackground: false,
      error: null,
      playingAudioId: null,
    };

    this.currentAudioElement = null;

    this.toggleModal = this.toggleModal.bind(this);
    this.onAudioSelect = this.onAudioSelect.bind(this);
    this.onGenerate = this.onGenerate.bind(this);
    this.loadSceneAudios = this.loadSceneAudios.bind(this);
    this.togglePreviewPlayback = this.togglePreviewPlayback.bind(this);
    this.stopPreviewPlayback = this.stopPreviewPlayback.bind(this);
  }

  componentDidMount() {
    window.addEventListener("keyup", this.toggleModal);
    this.loadSceneAudios();
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.loadSceneAudios();
    }
    if (prevProps.visible && !this.props.visible) {
      this.stopPreviewPlayback();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this.toggleModal);
    this.stopPreviewPlayback();
  }

  stopPreviewPlayback() {
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement.currentTime = 0;
      this.currentAudioElement = null;
    }
    if (this.state.playingAudioId !== null) {
      this.setState({ playingAudioId: null });
    }
  }

  togglePreviewPlayback(audio, index) {
    if (this.state.playingAudioId === index) {
      this.stopPreviewPlayback();
      return;
    }
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement = null;
    }
    const audioUrl = audio.file || audio.url;
    if (!audioUrl) return;
    const audioElement = new Audio(audioUrl);
    audioElement.addEventListener("ended", () => {
      if (this.currentAudioElement === audioElement) {
        this.currentAudioElement = null;
        this.setState({ playingAudioId: null });
      }
    });
    this.currentAudioElement = audioElement;
    this.setState({ playingAudioId: index });
    audioElement.play().catch(() => {
      if (this.currentAudioElement === audioElement) {
        this.currentAudioElement = null;
        this.setState({ playingAudioId: null });
      }
    });
  }

  toggleModal(e) {
    let key = e.keyCode ? e.keyCode : e.which;
    if (key === 27 && this.props.visible) {
      this.props.onClose();
    }
  }

  loadSceneAudios() {
    let sceneAudios = [];

    sceneAudios = this.props.getSceneAudios();
    this.setState({ sceneAudios });
  }

  onAudioSelect(audio) {
    this.setState({ selectedAudio: audio });
  }

  async onGenerate() {
    const { selectedAudio } = this.state;
    const { videoUrl, onLipsyncComplete } = this.props;

    if (!selectedAudio || !videoUrl) {
      alert(
        i18n.t("lipsync_select_audio_video_error") ||
          "Please select an audio and ensure video is available"
      );
      return;
    }

    this.setState({ isLoading: true, error: null });

    try {
      const formData = new FormData();

      // Add video file - we'll need to fetch it first
      const videoResponse = await fetch(videoUrl, credentialsFor(videoUrl));
      if (!videoResponse.ok) {
        throw new Error(`Failed to fetch video: ${videoResponse.status}`);
      }
      const videoBlob = await videoResponse.blob();
      formData.append("video", videoBlob, "video.mp4");

      // Add audio file - use the correct property (file or url)
      const audioUrl = selectedAudio.file || selectedAudio.url;
      if (!audioUrl) {
        throw new Error("Selected audio does not have a valid URL");
      }

      const audioResponse = await fetch(audioUrl, credentialsFor(audioUrl));
      if (!audioResponse.ok) {
        throw new Error(`Failed to fetch audio: ${audioResponse.status}`);
      }
      const audioBlob = await audioResponse.blob();
      // Ensure the blob has an audio/* MIME type so getMediaDuration accepts it.
      const typedAudio =
        audioBlob.type && audioBlob.type.startsWith("audio/")
          ? audioBlob
          : new Blob([audioBlob], { type: "audio/mpeg" });
      formData.append("audio", typedAudio, "audio.mp3");

      const durationSeconds =
        selectedAudio?.durationSeconds ??
        selectedAudio?.duration ??
        (await getMediaDuration(typedAudio));

      const queryParams = new URLSearchParams();
      queryParams.set("removeBackground", String(this.state.removeBackground));
      // Avatar videoları her zaman kare (1:1); backend lipsync çıktısını kırpar.
      queryParams.set("aspectRatio", "1:1");
      if (durationSeconds != null && !isNaN(Number(durationSeconds))) {
        queryParams.set("durationSeconds", String(Math.round(Number(durationSeconds))));
      }

      const response = await apiFetch(
        window.ideaLibraryRootUrl + "api/lipsync/processLipsync?" + queryParams.toString(),
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.videoUrl) {
        await this.uploadToLibraryAndReplace(result.videoUrl);

        // Call the completion callback
        if (onLipsyncComplete) {
          onLipsyncComplete(result.videoUrl);
        }

        // Close the modal
        this.setState({ isLoading: false });
        this.props.onClose();

        // Show success message
        alert(
          i18n.t("lipsync_success_message") ||
            "Lipsyncing completed successfully! The video has been updated with the new lipsynced version."
        );
      } else {
        throw new Error("No video URL returned from API");
      }
    } catch (error) {
      console.error("Lipsyncing error:", error);
      this.setState({
        error: `Lipsyncing failed: ${error.message}`,
        isLoading: false,
      });
    }
  }

  async uploadToLibraryAndReplace(lipsyncedVideoUrl) {
    try {
      // If no upload function is provided, fall back to directly updating the video URL
      if (!this.props.uploadFunction) {
        if (this.props.onToolbarUpdated) {
          const currentPluginId = this.props.editorProps?.pluginToolbar?.id;
          if (currentPluginId) {
            this.props.onToolbarUpdated(currentPluginId, "main", "state", "url", lipsyncedVideoUrl);
          }
        }

        return {
          success: false,
          error: "Upload function not available; used lipsynced URL directly",
          url: lipsyncedVideoUrl,
        };
      }

      // Fetch the lipsynced video
      const response = await fetch(lipsyncedVideoUrl, credentialsFor(lipsyncedVideoUrl));
      if (!response.ok) {
        throw new Error(`Failed to fetch lipsynced video: ${response.status}`);
      }

      const videoBlob = await response.blob();

      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `lipsynced_video_${timestamp}.mp4`;

      // Create a File object from the blob
      const videoFile = new File([videoBlob], fileName, { type: "video/mp4" });

      // Use the existing upload system through uploadFunction prop
      return new Promise((resolve, reject) => {
        try {
          // Call the uploadFunction with the video file
          this.props.uploadFunction(
            videoFile,
            ["lipsynced", "video", "generated"], // keywords
            (uploadedUrl) => {
              // Update the current video element's URL to the new lipsynced version
              if (this.props.onToolbarUpdated) {
                const currentPluginId = this.props.editorProps?.pluginToolbar?.id;
                if (currentPluginId) {
                  // Use the uploaded URL for the plugin
                  this.props.onToolbarUpdated(currentPluginId, "main", "state", "url", uploadedUrl);
                }
              }

              resolve({
                success: true,
                fileName: fileName,
                uploadedUrl: uploadedUrl,
                originalUrl: lipsyncedVideoUrl,
              });
            },
            (percentage) => {
              // Optional progress callback
            }
          );
        } catch (error) {
          console.error("Error during upload:", error);
          reject(error);
        }
      });
    } catch (error) {
      console.error("Error uploading to library:", error);

      // Even if upload to library fails, we can still update the video URL
      // This ensures the lipsyncing functionality works even if library storage fails
      try {
        if (this.props.onToolbarUpdated) {
          const currentPluginId = this.props.editorProps?.pluginToolbar?.id;
          if (currentPluginId) {
            // Use the original lipsynced URL directly
            this.props.onToolbarUpdated(currentPluginId, "main", "state", "url", lipsyncedVideoUrl);
          }
        }

        return {
          success: false,
          error: error.message,
          url: lipsyncedVideoUrl, // Still return the URL so it can be used
        };
      } catch (fallbackError) {
        console.error("Fallback update also failed:", fallbackError);
        throw error; // Re-throw the original error
      }
    }
  }

  render() {
    const { visible, onClose } = this.props;
    const { selectedAudio, sceneAudios, isLoading, error } = this.state;

    const footer = (
      <div className="app-modal__footer">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginRight: "auto",
          }}
        >
          <ToggleSwitch
            checked={this.state.removeBackground}
            onChange={() => {
              this.setState({ removeBackground: !this.state.removeBackground });
            }}
          />
          <p className="text-muted" style={{ margin: 0 }}>
            {i18n.t("lipsync_background_removal") || "Background Removal"}
          </p>
        </div>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          onClick={onClose}
          disabled={isLoading}
        >
          {i18n.t("Cancel")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          onClick={this.onGenerate}
          disabled={!selectedAudio || isLoading}
        >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {i18n.t("lipsync_generating") || "Generating..."}
            </>
          ) : (
            i18n.t("lipsync_generate") || "Generate"
          )}
        </button>
      </div>
    );

    return (
      <AppModal
        className="lipsyncModal"
        backdrop
        show={visible}
        onClose={onClose}
        title={i18n.t("lipsync_title") || "Lipsyncing"}
        size="lg"
        footer={footer}
      >
        <Row>
          <Col md={12}>
            <h5>{i18n.t("lipsync_select_narration") || "Select Narration Sound"}</h5>
            <p className="text-muted">
              {i18n.t("lipsync_choose_audio") ||
                "Choose an audio track from the scene to sync with your video"}
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                <strong>{i18n.t("Error") || "Error"}:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => this.setState({ error: null })}
                  style={{ float: "right" }}
                ></button>
              </div>
            )}

            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <ListGroup>
                {sceneAudios.map((audio, index) => (
                  <ListGroup.Item
                    key={audio.id ?? index}
                    active={selectedAudio && selectedAudio.id === audio.id}
                    action
                    onClick={() => this.onAudioSelect(audio)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{audio.name}</strong>
                        <br />
                        <small className="text-muted">
                          Duration: {audio.startTime}-{audio.endTime}
                        </small>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="app-modal__btn app-modal__btn--ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.togglePreviewPlayback(audio, index);
                          }}
                        >
                          <i className="material-icons">
                            {this.state.playingAudioId === index ? "stop" : "play_arrow"}
                          </i>
                        </button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>

            {selectedAudio && (
              <div className="selected-audio-info">
                <h6>Selected Audio:</h6>
                <p>
                  <strong>{selectedAudio.name}</strong>
                </p>
                <p>
                  <small>Duration: {selectedAudio.duration}</small>
                </p>
              </div>
            )}

            {sceneAudios.length === 0 && (
              <div className="no-audio-message">
                <p className="text-muted">No audio tracks found in this scene</p>
              </div>
            )}
          </Col>
        </Row>
      </AppModal>
    );
  }
}

LipsyncModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  removeBackground: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLipsyncComplete: PropTypes.func.isRequired,
  videoUrl: PropTypes.string.isRequired,
  editorProps: PropTypes.object,
  base: PropTypes.object,
  getSceneAudios: PropTypes.func,
  onToolbarUpdated: PropTypes.func,
  uploadFunction: PropTypes.func,
};
